import React, { useState } from 'react';
import { 
  Printer, 
  X, 
  BookOpen, 
  Stethoscope, 
  GraduationCap, 
  FileText, 
  CheckCircle2, 
  Download, 
  Sparkles, 
  ShieldCheck,
  Layers,
  HelpCircle,
  Loader2
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

export type PrintScope = 'study_guide' | 'skills_lab' | 'combined' | 'current';

interface PrintStudyGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmPrint: (scope: PrintScope) => void;
  onConfirmDownloadPdf?: (scope: PrintScope) => void;
  isGeneratingPdf?: boolean;
  currentTitle?: string;
  isCurrentLessonActive?: boolean;
  isCurrentSkillActive?: boolean;
  totalModulesCount?: number;
  totalSkillsCount?: number;
}

export const PrintStudyGuideModal: React.FC<PrintStudyGuideModalProps> = ({
  isOpen,
  onClose,
  onConfirmPrint,
  onConfirmDownloadPdf,
  isGeneratingPdf = false,
  currentTitle,
  isCurrentLessonActive = false,
  isCurrentSkillActive = false,
  totalModulesCount = 12,
  totalSkillsCount = 22
}) => {
  const tokens = useThemeTokens();
  const [selectedScope, setSelectedScope] = useState<PrintScope>(
    isCurrentLessonActive || isCurrentSkillActive ? 'current' : 'combined'
  );

  if (!isOpen) return null;

  const handlePrint = (scope: PrintScope) => {
    onConfirmPrint(scope);
  };

  return (
    <div 
      className="fixed inset-0 z-[1000] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4 md:p-6 animate-fade-in no-print overflow-y-auto custom-scrollbar"
      role="dialog"
      aria-modal="true"
      aria-labelledby="print-modal-title"
    >
      <div className={`bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-800 animate-pop my-8 relative ${tokens.accentBorder}`}>
        {/* Header */}
        <div className={`p-6 md:p-8 text-white relative overflow-hidden ${
          tokens.isWarmAcademic 
            ? 'bg-[#0D6E6E]' 
            : tokens.isClinical 
            ? 'bg-gradient-to-r from-[#0B2942] to-[#0F7D6C]' 
            : tokens.isHighContrast 
            ? 'bg-black text-amber-400 border-b-4 border-black' 
            : 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900'
        }`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/2" />
          
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-white/10 text-white text-[9px] font-black rounded-full uppercase tracking-widest border border-white/20 flex items-center gap-1.5">
              <Printer size={12} /> Printer-Optimized Export
            </span>
          </div>

          <h2 id="print-modal-title" className={`text-2xl md:text-3xl font-black text-white tracking-tight ${tokens.headerFont}`}>
            Print Study Guide & Skill Lab
          </h2>
          <p className="text-xs md:text-sm text-slate-200 mt-1.5 font-medium leading-relaxed max-w-lg">
            Formats current study material into a clean, printer-friendly PDF document with zero UI clutter, sidebars, or ads.
          </p>
        </div>

        {/* Scope Options */}
        <div className="p-6 md:p-8 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Select Material to Export & Print
          </p>

          {/* Option: Current Active Lesson/Skill (If Active) */}
          {(isCurrentLessonActive || isCurrentSkillActive) && (
            <button
              onClick={() => setSelectedScope('current')}
              className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-start gap-4 ${
                selectedScope === 'current'
                  ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30 ring-4 ring-indigo-500/10'
                  : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
              }`}
            >
              <div className="p-3 bg-indigo-600 text-white rounded-xl shrink-0 mt-0.5">
                <FileText size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="text-sm md:text-base font-black text-slate-900 dark:text-white truncate">
                    Current Active Unit: {currentTitle || 'Active Module'}
                  </h3>
                  <span className="text-[9px] font-black px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-md shrink-0 uppercase">
                    Active Session
                  </span>
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                  Prints all slides, diagrams, key terms, and self-check questions for this specific study topic.
                </p>
              </div>
            </button>
          )}

          {/* Option: Combined Master Handbook */}
          <button
            onClick={() => setSelectedScope('combined')}
            className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-start gap-4 ${
              selectedScope === 'combined'
                ? 'border-emerald-600 bg-emerald-50/50 dark:bg-emerald-950/30 ring-4 ring-emerald-500/10'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
            }`}
          >
            <div className="p-3 bg-emerald-600 text-white rounded-xl shrink-0 mt-0.5 shadow-md">
              <GraduationCap size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-sm md:text-base font-black text-slate-900 dark:text-white">
                  Combined Clinical Master Handbook
                </h3>
                <span className="text-[9px] font-black px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 rounded-md shrink-0 uppercase">
                  Complete Bundle
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                Includes all {totalModulesCount} Theoretical Study Modules + all {totalSkillsCount} Clinical Skills Lab Procedures in one comprehensive study manual.
              </p>
            </div>
          </button>

          {/* Option: Theoretical Study Guide */}
          <button
            onClick={() => setSelectedScope('study_guide')}
            className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-start gap-4 ${
              selectedScope === 'study_guide'
                ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/30 ring-4 ring-indigo-500/10'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
            }`}
          >
            <div className="p-3 bg-indigo-500 text-white rounded-xl shrink-0 mt-0.5">
              <BookOpen size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-sm md:text-base font-black text-slate-900 dark:text-white">
                  Theoretical Study Guide & Syllabus
                </h3>
                <span className="text-[9px] font-black px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md shrink-0">
                  {totalModulesCount} Lectures
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                Full syllabus of theoretical board reasoning lectures, exam traps, and key clinical vocabulary definitions.
              </p>
            </div>
          </button>

          {/* Option: Clinical Skills Lab Manual */}
          <button
            onClick={() => setSelectedScope('skills_lab')}
            className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-start gap-4 ${
              selectedScope === 'skills_lab'
                ? 'border-teal-600 bg-teal-50/50 dark:bg-teal-950/30 ring-4 ring-teal-500/10'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40'
            }`}
          >
            <div className="p-3 bg-teal-600 text-white rounded-xl shrink-0 mt-0.5">
              <Stethoscope size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="text-sm md:text-base font-black text-slate-900 dark:text-white">
                  Clinical Skills Procedure Manual
                </h3>
                <span className="text-[9px] font-black px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md shrink-0">
                  {totalSkillsCount} Procedures
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                Complete clinical skills step-by-step procedures, rationales, required equipment, and evaluator checklists.
              </p>
            </div>
          </button>

          {/* Helpful Tip */}
          <div className="p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-2xl flex items-start gap-3 text-amber-900 dark:text-amber-200 text-xs font-medium">
            <Sparkles size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <p>
              <strong>Pro Tip:</strong> When your system print dialog opens, choose <strong>"Save as PDF"</strong> under Destination to save a permanent digital copy to your computer or tablet!
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-slate-50 dark:bg-slate-950/80 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <button
            onClick={onClose}
            disabled={isGeneratingPdf}
            className="w-full sm:w-auto px-5 py-3 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-extrabold uppercase tracking-wider transition-all disabled:opacity-50"
          >
            Cancel
          </button>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            {onConfirmDownloadPdf && (
              <button
                onClick={() => onConfirmDownloadPdf(selectedScope)}
                disabled={isGeneratingPdf}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg disabled:opacity-50"
                id="modal-confirm-download-pdf-btn"
                title="Generate PDF file directly using jsPDF"
              >
                {isGeneratingPdf ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                <span>{isGeneratingPdf ? 'Generating PDF...' : 'Download PDF File'}</span>
              </button>
            )}

            <button
              onClick={() => handlePrint(selectedScope)}
              disabled={isGeneratingPdf}
              className={`w-full sm:w-auto px-6 py-3.5 rounded-xl text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-xl ${tokens.primaryBtn} disabled:opacity-50`}
              id="modal-confirm-print-btn"
              title="Open browser print dialog"
            >
              <Printer size={16} />
              <span>Print via Browser</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
